<template>
  <BaseModal
    is-open
    :title="title"
    size="small"
    @close="close"
  >
    <div class="text-sm">
      Save your dream venues and return when you're ready to book.
    </div>

    <div class="flex gap-4 flex-col my-6">
      <FacebookLoginButton :api-config="apiConfig" :tracking-params="{...trackingParams, auth_method: 'facebook'}" text="Continue with Facebook" @click="onFacebookLoginClick" @success="onSocialSuccess" @error="setError" />
      <GoogleLoginButton :api-config="googleApiConfig" :tracking-params="{...trackingParams, auth_method: 'google'}" text="Continue with Google" @click="onGoogleLoginClick" @success="onSocialSuccess" @error="setError" />
    </div>

    <HorizontalLine text="or add your contact details" class="my-5" />

    <TextField
      v-model="form.name.value"
      :errors="form.name.errors"
      name="name"
      type="text"
      class="my-4"
      placeholder="Full name"
      @update:modelValue="form.name.errors = []"
    />
    <EmailField
      v-model="form.email.value"
      :errors="form.email.errors"
      class="my-4"
      placeholder="Email – watch for typos"
      @update:modelValue="form.email.errors = []"
    />
    <TextField
      v-model="form.password.value"
      :icon="LockIcon"
      :errors="form.password.errors"
      :is-marked-as-invalid="form.password.errors.length > 0"
      name="password"
      type="password"
      :maxlength="64"
      class="mb-1.5"
      placeholder="Create a password"
      @update:modelValue="form.password.errors = []"
    />

    <CheckboxField
      v-model="form.marketingConstent.value"
      :errors="form.marketingConstent.errors"
      class="mt-4 mb-1 items-start"
      label="I'd like to receive marketing promotions, special offers and inspiration from Tagvenue. I can opt out at any time."
      @update:modelValue="form.marketingConstent.errors = []"
    />

    <BaseButton color="green" :is-loading="isLoading" class="w-full mt-4" @click="login">
      Sign up
    </BaseButton>

    <ConsentsInfo classes="text-center leading-4 mt-3" />

    <div class="im:-mb-4 text-center">
      <HorizontalLine class="my-6" />
      Already have a User Account? <a class="font-semibold cursor-pointer" @click="onLoginClick">Log in</a>
    </div>
  </BaseModal>

  <Captcha :config="captcha" @onTokenResponse="(token) => { captcha.token = token; }" />
</template>
<script>
import LockIcon from '@tagvenue/icons/24/line/security/lock-01.svg';
import { markRaw } from 'vue';

import EmailField from '@/components/Forms/EmailField.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import FacebookLoginButton from '@/components/Ui/Buttons/FacebookLoginButton.vue';
import GoogleLoginButton from '@/components/Ui/Buttons/GoogleLoginButton.vue';
import CheckboxField from '@/components/Ui/Forms/CheckboxField.vue';
import TextField from '@/components/Ui/Forms/TextField.vue';
import HorizontalLine from '@/components/Ui/HorizontalLine.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import Captcha from '@/modules/public/enquiry-form/components/EnquiryForm/components/Captcha.vue';
import ConsentsInfo from '@/modules/venue-cabinet/vc-complete-profile/components/ConsentsInfo.vue';
import formValidation, { FORM_VALIDATION } from '@/services/formValidation';
import { csrfToken } from '@/utils';

export default {
  name: 'SignUpModal',
  emits: ['close', 'trackEvent', 'register', 'error', 'loginClick'],
  mixins: [formValidation],
  components: {
    ConsentsInfo,
    CheckboxField,
    TextField,
    EmailField,
    FacebookLoginButton,
    GoogleLoginButton,
    BaseButton,
    Captcha,
    HorizontalLine,
    BaseModal,
  },
  props: {
    title: {
      type: String,
      default: 'Sign up',
    },
    email: {
      type: String,
      required: false,
    },
    signUp: {
      type: Boolean,
      default: false,
    },
    trackingParams: {
      type: Object,
      required: true,
    },
    apiConfig: {
      type: Object,
      required: true,
    },
    googleApiConfig: {
      type: Object,
      required: true,
    },
    topText: {
      type: String,
    },
  },
  data() {
    return {
      isLoading: false,
      LockIcon: markRaw(LockIcon),
      form: {
        name: {
          value: this.name || '',
          errors: [],
        },
        email: {
          value: this.email || '',
          errors: [],
        },
        password: {
          value: '',
          errors: [],
        },
        marketingConstent: {
          value: false,
          errors: [],
        },
      },
      captcha: {
        sitekey: this.apiConfig?.captcha.sitekey,
        isEnabled: false,
        token: null,
      },
      formValidation: {
        email: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter your email address',
          [FORM_VALIDATION.EMAIL]: 'Please enter a valid email address',
        },
        password: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter your password',
        },
      },
    };
  },
  methods: {
    onSocialSuccess(response) {
      this.isLoading = true;

      this.$emit('register', { emailMatch: this.email === response?.email });
    },
    onFacebookLoginClick() {
      this.trackEvent('continue_with_facebook_button');
    },
    onGoogleLoginClick() {
      this.trackEvent('continue_with_google_button');
    },
    trackEvent(element, interaction = 'click') {
      this.$emit('trackEvent', { section: 'login_modal', element, interaction });
    },
    close() {
      this.isLoading = false;
      this.trackEvent('close_button');
      this.$emit('close');
    },
    setError(error) {
      this.$emit('error', error);
      this.isLoading = false;
    },
    login({ captcha = false } = {}) {
      const self = this;

      this.trackEvent('login_button');

      if (!this.validateFields()) {
        return;
      }

      this.isLoading = true;
      $.ajax({
        url: '/signup/client',
        type: 'POST',
        hideDefaultErrorMessage: true,
        data: {
          'g-recaptcha-response': this.captcha.token,
          _token: csrfToken(),
          name: this.form.name.value,
          email: this.form.email.value,
          password: this.form.password.value,
          trackingParams: this.trackingParams,
          marketing_emails_checkbox: this.form.marketingConstent.value ? '_on' : null,
        },
        success(data, type, xhr) {
          this.isLoading = false;

          if (xhr.status === 200) {
            self.$emit('register', { emailMatch: self.email === self.form.email.value });
          } else {
            self.setError('An error occurred. Sorry for the inconvenience.');
          }
        },
        error(response) {
          if (!captcha && response.responseJSON?.['g-recaptcha-response']) {
            self.captcha.token = null;
            self.captcha.isEnabled = true;
            console.log('Captcha requested');
            Rollbar.debug('Captcha requested');

            const unwatch = self.$watch('captcha.token', () => {
              self.isLoading = false;
              self.captcha.isEnabled = false;
              self.login({ captcha: true });
              unwatch();
            });

            return;
          }

          if (response.responseJSON.email && response.responseJSON.email[0] === 'Looks like you already have an account. Please log in instead.' && response.status === 422) {
            self.onLoginClick();
            return;
          }

          if (response.responseJSON.password) {
            self.form.password.errors = response.responseJSON.password;
          } else if (response.responseJSON.email) {
            self.form.email.errors = response.responseJSON.email;
          } else if (response.responseJSON.name) {
            self.form.name.errors = response.responseJSON.name;
          } else if (response.responseJSON.message) {
            self.setError(response.responseJSON.message);
          } else {
            self.setError('An error occurred. Sorry for the inconvenience.');
          }

          self.isLoading = false;
        },
      });
    },
    onLoginClick() {
      this.$emit('loginClick', this.form.email.value);
    },
  },
  mounted() {
    this.trackEvent('modal', 'visible');
  },
};
</script>
